import {
  DESKTOP_PROTOCOL_VERSION,
  type DesktopCapabilityActivationResult,
  type DesktopCloseResult,
  type DesktopOperationRequest,
  type DesktopOperationResult,
} from "../../shared/schemas";
import type { ActiveAppFrame } from "../app-frames";

export type DesktopPreloadApi = {
  protocolVersion: 1;
  getAvailability(): Promise<unknown>;
  activateCapability(input: { protocolVersion: 1; challengeId: string }): Promise<unknown>;
  performOperation(input: {
    protocolVersion: 1;
    sessionHandle: string;
    operation: DesktopOperationRequest;
  }): Promise<unknown>;
  closeCapability(input: { protocolVersion: 1; sessionHandle: string }): Promise<unknown>;
  subscribe(listener: (event: unknown) => void): () => void;
  getDiagnostics(input: { protocolVersion: 1 }): Promise<unknown>;
};

export type OwnedSession = {
  source: WindowProxy;
  frame: ActiveAppFrame;
  capability:
    | "serial.balance"
    | "filesystem.user-selected"
    | "tcp.client"
    | "http.loopback-listener";
  allowedOperations: readonly string[];
};

export const DESKTOP_FRAME_BROKER_DEADLINES_MS = Object.freeze({
  availability: 10_000,
  controlPlane: 15_000,
  activation: 295_000,
  interactiveOperation: 295_000,
  operation: 40_000,
  close: 10_000,
});

export class DesktopFrameDeadlineError extends Error {
  readonly code: "CONTROL_PLANE_TIMEOUT" | "DESKTOP_TIMEOUT";

  constructor(code: "CONTROL_PLANE_TIMEOUT" | "DESKTOP_TIMEOUT", message: string) {
    super(message);
    this.name = "DesktopFrameDeadlineError";
    this.code = code;
  }
}

export function withDeadline<T>(
  promise: Promise<T>,
  timeoutMs: number,
  error: { code: DesktopFrameDeadlineError["code"]; message: string },
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      reject(new DesktopFrameDeadlineError(error.code, error.message));
    }, timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timeout);
        resolve(value);
      },
      (cause: unknown) => {
        window.clearTimeout(timeout);
        reject(
          cause instanceof Error ? cause : new Error("Desktop bridge request failed", { cause }),
        );
      },
    );
  });
}

export function getDesktopApi(): DesktopPreloadApi | null {
  const candidate = window.ccboxDesktop;
  if (!candidate || typeof candidate !== "object") return null;
  const api = candidate as Partial<DesktopPreloadApi>;
  const valid =
    api.protocolVersion === DESKTOP_PROTOCOL_VERSION &&
    typeof api.getAvailability === "function" &&
    typeof api.activateCapability === "function" &&
    typeof api.performOperation === "function" &&
    typeof api.closeCapability === "function" &&
    typeof api.subscribe === "function" &&
    typeof api.getDiagnostics === "function";
  return valid ? (api as DesktopPreloadApi) : null;
}

export function activationError(
  code: string,
  message: string,
  operationId: string,
  retryable = false,
): DesktopCapabilityActivationResult {
  return failureResult(code, message, operationId, retryable);
}

export function operationError(
  code: string,
  message: string,
  operationId: string,
  retryable = false,
): DesktopOperationResult {
  return failureResult(code, message, operationId, retryable);
}

export function closeError(
  code: string,
  message: string,
  operationId: string,
  retryable = false,
): DesktopCloseResult {
  return failureResult(code, message, operationId, retryable);
}

function failureResult(code: string, message: string, operationId: string, retryable: boolean) {
  return {
    protocolVersion: DESKTOP_PROTOCOL_VERSION,
    ok: false as const,
    error: { code, message, retryable, operationId },
  };
}

export function correlateResult<
  T extends DesktopCapabilityActivationResult | DesktopOperationResult | DesktopCloseResult,
>(result: T, requestId: string): T {
  return result.ok ? result : { ...result, error: { ...result.error, operationId: requestId } };
}
